import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useContext } from 'react';
import { TextFieldElement } from 'react-hook-form-mui';
import { APIContext, DialogAction } from '../Contexts/APIContext';
import PDFLink from '../Popups/PDFLink';
import DataContainer from '../Utilities/DataContainer';
import GetDftConnectivityToolScoreViewModel from './ViewModels/GetDftConnectivityToolScoreViewModel';

const headerColour = '#1aa6c8';
const scoreFieldWidth = 82;
const scoreRowSx = (readOnly: boolean) => ({
    minHeight: 40,
    ...(readOnly ? { height: { md: 40 } } : {}),
    '& > *': { minWidth: 0 }
});

export const hasDftConnectivityToolScore = (score?: GetDftConnectivityToolScoreViewModel | null) => Boolean(
    score?.squareId
    || score?.connectivityScore != null
    || score?.drivingConnectivityScore != null
    || score?.localAuthorityName
    || score?.localAuthorityBand
    || score?.walkingConnectivityScore != null
    || score?.cyclingConnectivityScore != null
    || score?.publicTransportConnectivityScore != null
    || score?.residentialDevelopmentsScore != null
    || score?.educationDevelopmentsScore != null
    || score?.leisureDevelopmentsScore != null
    || score?.healthDevelopmentsScore != null
    || score?.shoppingDevelopmentsScore != null
    || score?.workplaceDevelopmentsScore != null
);

const fieldSx = {
    width: '100%',
    '& .MuiInputBase-root': {
        backgroundColor: 'white',
        borderRadius: 1.5,
        height: 40
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: 1.5
    },
    '& .MuiInputBase-input': {
        textAlign: 'right',
        py: 0.4,
        px: 1
    },
    '& input[type=number]': {
        MozAppearance: 'textfield'
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
    }
};

const ReadOnlyField = ({ value, numeric = false, truncate = false }: {
    value: string | number | null | undefined;
    numeric?: boolean;
    truncate?: boolean;
}) => {
    const displayValue = value?.toString() ?? '';
    const field = <TextField
        value={value ?? ''}
        size="small"
        inputProps={{ readOnly: true }}
        sx={{
            ...fieldSx,
            ...(numeric ? { maxWidth: scoreFieldWidth } : {}),
            ...(truncate ? {
                '& .MuiInputBase-input.MuiInputBase-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }
            } : {})
        }}
    />;

    return truncate && displayValue
        ? <Tooltip title={displayValue} arrow describeChild><span style={{ display: 'block', width: '100%' }}>{field}</span></Tooltip>
        : field;
};

const TextScoreRow = ({ label, name, value, readOnly }: { label: string; name: string; value: string | null; readOnly: boolean }) => (
    <Grid container size={12} alignItems="center" spacing={0.75} sx={scoreRowSx(readOnly)}>
        <Grid size={{ xs: 8 }}><Typography variant="body2">{label}</Typography></Grid>
        <Grid size={{ xs: 4 }}>
            {readOnly
                ? <ReadOnlyField value={value} />
                : <TextFieldElement name={name} size="small" fullWidth sx={fieldSx} />}
        </Grid>
    </Grid>
);

const NumberScoreRow = ({ label, name, value, readOnly }: { label: string; name: string; value: number | null; readOnly: boolean }) => (
    <Grid container size={12} alignItems="center" spacing={0.75} sx={scoreRowSx(readOnly)}>
        <Grid size="grow"><Typography variant="body2">{label}</Typography></Grid>
        <Grid sx={{ width: scoreFieldWidth, flexShrink: 0 }}>
            {readOnly
                ? <ReadOnlyField value={value} numeric />
                : <TextFieldElement
                    name={name}
                    type="number"
                    size="small"
                    rules={{
                        validate: (fieldValue) => {
                            if (fieldValue == null || fieldValue === '') return true;
                            if (!Number.isInteger(Number(fieldValue))) return 'Please enter a whole number';
                            return (Number(fieldValue) >= 0 && Number(fieldValue) <= 100) || 'Score must be between 0 and 100';
                        }
                    }}
                    transform={{
                        input: (fieldValue) => fieldValue ?? '',
                        output: (event) => {
                            const inputValue = event.target.value.trim();
                            if (inputValue === '') return null as never;
                            const parsedValue = Number(inputValue);
                            return (Number.isNaN(parsedValue) ? null : parsedValue) as never;
                        }
                    }}
                    inputProps={{ step: 1, min: 0, max: 100, inputMode: 'numeric' }}
                    sx={fieldSx}
                />}
        </Grid>
    </Grid>
);

const LocalAuthorityRow = ({ score, prefix, readOnly }: { score: GetDftConnectivityToolScoreViewModel; prefix: string; readOnly: boolean }) => (
    <Grid container size={12} alignItems="center" spacing={0.75} sx={scoreRowSx(readOnly)}>
        <Grid size="grow"><Typography variant="body2">Local Authority Band Score</Typography></Grid>
        <Grid sx={{ width: { xs: 180, sm: 220 }, flexShrink: 1, minWidth: 0 }}>
            {readOnly
                ? <ReadOnlyField value={score.localAuthorityName} truncate />
                : <TextFieldElement name={`${prefix}.localAuthorityName`} size="small" fullWidth sx={fieldSx} />}
        </Grid>
        <Grid sx={{ width: scoreFieldWidth, flexShrink: 0 }}>
            {readOnly
                ? <ReadOnlyField value={score.localAuthorityBand} />
                : <TextFieldElement name={`${prefix}.localAuthorityBand`} size="small" fullWidth sx={fieldSx} />}
        </Grid>
    </Grid>
);

interface IProps {
    score?: GetDftConnectivityToolScoreViewModel | null;
    eligible: boolean;
    enabled: boolean;
    readOnly: boolean;
    fieldPrefix?: string;
    travelPlanLayout?: boolean;
}

export default function DftConnectivityToolScores({ score, eligible, enabled, readOnly, fieldPrefix = 'dftConnectivityToolScore', travelPlanLayout = false }: IProps) {
    const { openDialog } = useContext(APIContext);
    const values = score ?? new GetDftConnectivityToolScoreViewModel();

    if (!eligible || (readOnly && (!enabled || !hasDftConnectivityToolScore(values)))) return null;

    const openWebsite = () => {
        openDialog(
            'Access to the full DfT Connectivity Tool is only available to users approved by the Department for Transport. If you have not been approved, you can use the Lite version, which does not include the full functionality.',
            [new DialogAction('Continue to website', () => window.open('https://www.gov.uk/guidance/connectivity-tool#start-using-the-connectivity-tool', '_blank', 'noopener,noreferrer'))],
            'DfT Connectivity Tool access',
            'Cancel'
        );
    };

    const numberRow = (label: string, field: keyof GetDftConnectivityToolScoreViewModel) => (
        <NumberScoreRow label={label} name={`${fieldPrefix}.${field}`} value={values[field] as number | null} readOnly={readOnly} />
    );

    return (
        <Grid
            container
            sx={{
                mt: 1,
                width: { xs: '100%', md: travelPlanLayout ? '66.667%' : '100%' },
                maxWidth: { xs: '100%', md: travelPlanLayout ? '66.667%' : '100%' }
            }}
        >
            <DataContainer
                title="DfT Connectivity Tool Scores"
                hideDivider
                containerSx={{ border: `1px solid ${headerColour}`, borderRadius: '10px', overflow: 'hidden', p: 0 }}
                headerRight={
                    <PDFLink
                        container="GeneralDocs"
                        file="DfT Connectivity Scores in TRICS"
                        tooltip="Download DfT Connectivity Tool help document"
                        iconColor="inherit"
                        iconButtonSx={{ color: 'white', p: 0.5 }}
                    />
                }
                headerSx={{
                    backgroundColor: headerColour,
                    px: 1.25,
                    py: 0.5,
                    color: 'white',
                    '& .MuiTypography-root': { color: 'white', fontWeight: 600 },
                    '& > :last-child': { ml: 'auto' }
                }}
            >
                <Grid container size={12} spacing={1} sx={{ p: 1 }}>
                    <Grid
                        container
                        size={{ xs: 12, md: 6 }}
                        spacing={0.75}
                        sx={{
                            pr: { md: 1.25 },
                            borderRight: { md: '1px solid #7ecad4' }
                        }}
                    >
                        <Grid container size={12} alignItems="center" spacing={0.75} sx={scoreRowSx(readOnly)}>
                            <Grid size="grow"><Typography variant="body2">DfT Connectivity Tool</Typography></Grid>
                            <Grid><Button onClick={openWebsite} variant="outlined" size="small" sx={{ textTransform: 'none', py: 0.25 }}>Connectivity Tool website (full/lite versions)</Button></Grid>
                        </Grid>
                        {numberRow('Connectivity Score', 'connectivityScore')}
                        {numberRow('Driving Connectivity Score', 'drivingConnectivityScore')}
                        <LocalAuthorityRow score={values} prefix={fieldPrefix} readOnly={readOnly} />
                        {numberRow('Walking Connectivity Score', 'walkingConnectivityScore')}
                        {numberRow('Cycling Connectivity Score', 'cyclingConnectivityScore')}
                        {numberRow('Public Transport Connectivity Score', 'publicTransportConnectivityScore')}
                    </Grid>
                    <Grid container size={{ xs: 12, md: 6 }} spacing={0.75} sx={{ pl: { md: 0.5 } }}>
                        <TextScoreRow label="Connectivity Tool Square ID" name={`${fieldPrefix}.squareId`} value={values.squareId} readOnly={readOnly} />
                        {numberRow('Connectivity to Local Residential Developments Score', 'residentialDevelopmentsScore')}
                        {numberRow('Connectivity to Local Education Developments Score', 'educationDevelopmentsScore')}
                        {numberRow('Connectivity to Local Leisure Developments Score', 'leisureDevelopmentsScore')}
                        {numberRow('Connectivity to Local Health Developments Score', 'healthDevelopmentsScore')}
                        {numberRow('Connectivity to Local Shopping Developments Score', 'shoppingDevelopmentsScore')}
                        {numberRow('Connectivity to Local Workplace Developments Score', 'workplaceDevelopmentsScore')}
                    </Grid>
                </Grid>
            </DataContainer>
        </Grid>
    );
}
